import React, { useReducer, useState, useEffect } from 'react';
import { 
    Box, Typography, Button, Paper, Chip, IconButton, 
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    MenuItem, Grid, InputAdornment, Switch, FormControlLabel, alpha, useTheme 
} from '@mui/material';
import MUIDataTable from "mui-datatables";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, faEdit, faTrash, faCode, faCrown, faBuilding, 
    faRocket, faWallet, faUsers, faChartPie, faDollarSign, faVial, faClock, 
    faStore
} from '@fortawesome/free-solid-svg-icons';
import { listPlans } from '../../../controllers/superadmin/subscriptions/PlansController';
import { createPlan } from '../../../controllers/company/subscriptions/PlansController';
import QUDProgress from '../../../components/progress';

// --- ESTRUCTURA DEL REDUCER ---
const initialState = {
    formData: {
        name: '',
        code: '',
        description: '',
        price: '',
        billing_interval: 'month',
        interval_count: 1,
        trial_days: 0,
        status: true
    },
    errors: {}
};

function formReducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return {
                ...state,
                formData: { ...state.formData, [action.field]: action.value },
                errors: { ...state.errors, [action.field]: false } // Limpia el error al escribir
            };
        case 'SET_ERRORS':
            return { ...state, errors: action.errors };
        case 'LOAD_PLAN':
            return { ...state, formData: action.payload, errors: {} };
        case 'RESET_FORM':
            return initialState;
        default:
            return state;
    }
}

export default function AdminPlans() {
    const theme = useTheme();
    const [state, dispatch] = useReducer(formReducer, initialState);
    const { formData, errors } = state;

    const [plans, setPlans] = useState([]);
    const [kpis, setKpis] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [isLoading,setIsLoading] = useState(false);

    // Carga inicial (Simulada)
    useEffect(() => {
        handleGetPlansData();
    }, []);


    const handleGetPlansData = async () => {
        setIsLoading(true);
        const { kpis, recent_activity, plans } = await listPlans();
        setPlans(plans);
        setKpis(kpis);
        setIsLoading(false);
    }


    // --- LÓGICA DE VALIDACIÓN ---
    const validateForm = () => {
        let tempErrors = {};
        if (!formData.name.trim()) tempErrors.name = "El nombre es obligatorio";
        if (!formData.price || formData.price < 0) tempErrors.price = "Precio inválido";
        if (!formData.interval_count || formData.interval_count < 1) tempErrors.interval_count = "Mínimo 1";
        
        dispatch({ type: 'SET_ERRORS', errors: tempErrors });
        return Object.keys(tempErrors).length === 0;
    };

    // --- MANEJADORES ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        dispatch({ 
            type: 'SET_FIELD', 
            field: name, 
            value: type === 'checkbox' ? checked : value 
        });
    };

    const handleOpenModal = (plan = null) => {
        if (plan) {
            setEditingId(plan.id);
            dispatch({ type: 'LOAD_PLAN', payload: { ...plan, status: Boolean(plan.status) } });
        } else {
            setEditingId(null);
            dispatch({ type: 'RESET_FORM' });
        }
        setOpenModal(true);
    };

    const handleSave = async () => {
        if (validateForm()) {
            setIsLoading(true);
            const response = await createPlan(formData)
            if(response.success)
            {
                console.log("RESPONSE DE CREATE ===> ", response);
                handleGetPlansData();
            }
            
            setOpenModal(false);
            setIsLoading(false)
        }
    };

    const getIntervalPreview = () => {
        const { interval_count, billing_interval } = formData;
        const labels = { day: 'día', week: 'semana', month: 'mes', year: 'año' };
        const plural = interval_count > 1 ? (billing_interval === 'month' ? 'es' : 's') : '';
        return `Se cobrará cada ${interval_count} ${labels[billing_interval]}${plural}.`;
    };

    // --- COMPONENTES UI ---
    const MiniStatCard = ({ title, value, icon, color }) => (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #F1F5F9', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, position: 'relative', zIndex: 2 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: 3, bgcolor: alpha(color, 0.1), color: color, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={icon} size="lg" />
                </Box>
            </Box>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>{value}</Typography>
                <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>{title}</Typography>
            </Box>
            <Box sx={{ position: 'absolute', right: -20, top: -20, width: 100, height: 100, borderRadius: '50%', bgcolor: alpha(color, 0.05) }} />
        </Paper>
    );

    const columns = [
        {
            name: "plan_info",
            label: "PLAN",
            options: {
                customBodyRenderLite: (dataIndex) => {
                    const plan = plans[dataIndex];
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: alpha(plan.color || '#64748b', 0.1), color: plan.color || '#64748b', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <FontAwesomeIcon icon={plan.icon || faBuilding} />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>{plan.name}</Typography>
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>{plan.billing_interval}ly</Typography>
                            </Box>
                        </Box>
                    );
                }
            }
        },
        {
            name: "code",
            label: "CÓDIGO",
            options: {
                customBodyRender: (value) => (
                    <Chip label={value} size="small" sx={{ fontFamily: 'monospace', fontWeight: 600, bgcolor: '#F1F5F9', borderRadius: 1 }} />
                )
            }
        },
        {
            name: "price",
            label: "PRECIO",
            options: { customBodyRender: (v) => <Typography variant="body2" sx={{ fontWeight: 800 }}>${v}</Typography> }
        },
        {
            name: "status",
            label: "ESTADO",
            options: {
                customBodyRender: (v) => (
                    <Chip label={v ? 'Activo' : 'Inactivo'} size="small" sx={{ fontWeight: 800, bgcolor: v ? '#ECFDF5' : '#F1F5F9', color: v ? '#10B981' : '#64748B', borderRadius: 2 }} />
                )
            }
        },
        {
            name: "id",
            label: " ",
            options: {
                filter: false, sort: false,
                customBodyRenderLite: (dataIndex) => (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton size="small" onClick={() => handleOpenModal(plans[dataIndex])}><FontAwesomeIcon icon={faEdit} size="sm" /></IconButton>
                        <IconButton size="small" color="error"><FontAwesomeIcon icon={faTrash} size="sm" /></IconButton>
                    </Box>
                )
            }
        }
    ];

    return (
        <Box sx={{ pb: 5 }}>
            <QUDProgress open={isLoading}/>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>Planes</Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>Gestión de suscripciones B2B y B2C.</Typography>
                </Box>
                <Button 
                    variant="contained" disableElevation startIcon={<FontAwesomeIcon icon={faPlus} />} 
                    onClick={() => handleOpenModal()}
                    sx={{ bgcolor: '#0F172A', borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 3, py: 1.2 }}
                >
                    Crear Plan
                </Button>
            </Box>

                <Grid container spacing={3} sx={{ mb: 5 }}>
                    <Grid item xs={12} md={4}><MiniStatCard title="Merchants" value={kpis.active_merchants} icon={faStore} color="#3b82f6" /></Grid>
                    <Grid item xs={12} md={4}><MiniStatCard title="Suscriptores" value={kpis.active_qud_passes} icon={faUsers} color="#10b981" /></Grid>
                    <Grid item xs={12} md={4}><MiniStatCard title="MRR" value={kpis.mrr} icon={faWallet} color="#8b5cf6" /></Grid>
                </Grid>
            
            

            <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid #F1F5F9', overflow: 'hidden' }}>
                {
                    plans.length !== 0
                    ?
                        <MUIDataTable
                        title={<Typography variant="h6" sx={{ fontWeight: 800 }}>Catálogo de Planes</Typography>}
                        data={plans}
                        columns={columns}
                        options={{ selectableRows: 'none', elevation: 0, responsive: 'standard' }}
                    />
                    :
                    <Typography>No se encontraron planes para listar</Typography>
                }
                
            </Paper>

            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{editingId ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={7}>
                            <TextField 
                                fullWidth label="Nombre del Plan" name="name"
                                value={formData.name} onChange={handleInputChange}
                                error={!!errors.name} helperText={errors.name}
                                InputProps={{ sx: { borderRadius: 2.5 } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <TextField 
                                fullWidth label="Código (Slug)" name="code"
                                value={formData.code} onChange={handleInputChange}
                                InputProps={{ sx: { borderRadius: 2.5, bgcolor: '#F8FAFC' }, startAdornment: <InputAdornment position="start"><FontAwesomeIcon icon={faCode} size="xs" /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth multiline rows={2} label="Descripción" name="description"
                                value={formData.description} onChange={handleInputChange}
                                InputProps={{ sx: { borderRadius: 2.5 } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField 
                                fullWidth type="number" label="Precio ($)" name="price"
                                value={formData.price} onChange={handleInputChange}
                                error={!!errors.price}
                                InputProps={{ sx: { borderRadius: 2.5 }, startAdornment: <InputAdornment position="start"><FontAwesomeIcon icon={faDollarSign} size="xs" /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField 
                                fullWidth type="number" label="Multiplicador" name="interval_count"
                                value={formData.interval_count} onChange={handleInputChange}
                                error={!!errors.interval_count}
                                InputProps={{ sx: { borderRadius: 2.5 } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField 
                                fullWidth select label="Frecuencia" name="billing_interval"
                                value={formData.billing_interval} onChange={handleInputChange}
                                InputProps={{ sx: { borderRadius: 2.5 } }}
                            >
                                <MenuItem value="day">Día(s)</MenuItem>
                                <MenuItem value="week">Semana(s)</MenuItem>
                                <MenuItem value="month">Mes(es)</MenuItem>
                                <MenuItem value="year">Año(s)</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2.5, border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FontAwesomeIcon icon={faClock} color={theme.palette.primary.main} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Resumen: {getIntervalPreview()}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField 
                                fullWidth type="number" label="Días de Prueba" name="trial_days"
                                value={formData.trial_days} onChange={handleInputChange}
                                InputProps={{ sx: { borderRadius: 2.5 }, startAdornment: <InputAdornment position="start"><FontAwesomeIcon icon={faVial} size="xs" /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ bgcolor: '#F8FAFC', p: 1, borderRadius: 2.5, border: '1px solid #F1F5F9', height: '100%', display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={<Switch color="primary" name="status" checked={formData.status} onChange={handleInputChange} />}
                                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{formData.status ? 'Activo' : 'Inactivo'}</Typography>}
                                    sx={{ ml: 1 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenModal(false)} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 700 }}>Cancelar</Button>
                    <Button variant="contained" disableElevation onClick={handleSave} sx={{ bgcolor: '#2563EB', borderRadius: 2.5, textTransform: 'none', fontWeight: 700, px: 3 }}>Guardar Plan</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}